/**
 * Theme constants and default tokens
 */

import type { ThemeDefinition } from './theme.types';

export const THEME_CSS_VARS = {
  colors: {
    background: '--background',
    foreground: '--foreground',
    border: '--border',
    input: '--input',
    ring: '--ring',
    card: {
      base: '--card',
      foreground: '--card-foreground',
      border: '--card-border',
    },
    popover: {
      base: '--popover',
      foreground: '--popover-foreground',
      border: '--popover-border',
    },
    primary: {
      base: '--primary',
      foreground: '--primary-foreground',
    },
    secondary: {
      base: '--secondary',
      foreground: '--secondary-foreground',
    },
    muted: {
      base: '--muted',
      foreground: '--muted-foreground',
    },
    accent: {
      base: '--accent',
      foreground: '--accent-foreground',
    },
    destructive: {
      base: '--destructive',
      foreground: '--destructive-foreground',
    },
    sidebar: {
      base: '--sidebar',
      foreground: '--sidebar-foreground',
      border: '--sidebar-border',
      primary: '--sidebar-primary',
      primaryForeground: '--sidebar-primary-foreground',
      accent: '--sidebar-accent',
      accentForeground: '--sidebar-accent-foreground',
      ring: '--sidebar-ring',
    },
    chart: {
      '1': '--chart-1',
      '2': '--chart-2',
      '3': '--chart-3',
      '4': '--chart-4',
      '5': '--chart-5',
    },
    outline: {
      button: '--button-outline',
      badge: '--badge-outline',
    },
    elevate: {
      '1': '--elevate-1',
      '2': '--elevate-2',
    },
    opaqueButtonBorderIntensity: '--opaque-button-border-intensity',
  },
  typography: {
    fontFamily: {
      sans: '--font-sans',
      serif: '--font-serif',
      mono: '--font-mono',
    },
    fontSize: {
      xs: '--font-size-xs',
      sm: '--font-size-sm',
      base: '--font-size-base',
      lg: '--font-size-lg',
      xl: '--font-size-xl',
      '2xl': '--font-size-2xl',
      '3xl': '--font-size-3xl',
      '4xl': '--font-size-4xl',
      '5xl': '--font-size-5xl',
      '6xl': '--font-size-6xl',
      '7xl': '--font-size-7xl',
      '8xl': '--font-size-8xl',
      '9xl': '--font-size-9xl',
    },
    lineHeight: {
      xs: '--line-height-xs',
      sm: '--line-height-sm',
      base: '--line-height-base',
      lg: '--line-height-lg',
      xl: '--line-height-xl',
      '2xl': '--line-height-2xl',
      '3xl': '--line-height-3xl',
      '4xl': '--line-height-4xl',
      '5xl': '--line-height-5xl',
      '6xl': '--line-height-6xl',
      '7xl': '--line-height-7xl',
      '8xl': '--line-height-8xl',
      '9xl': '--line-height-9xl',
    },
    fontWeight: {
      regular: '--font-weight-regular',
      medium: '--font-weight-medium',
      semibold: '--font-weight-semibold',
      bold: '--font-weight-bold',
    },
    letterSpacing: {
      tight: '--tracking-tight',
      normal: '--tracking-normal',
      wide: '--tracking-wide',
    },
  },
  radius: {
    sm: '--radius-sm',
    md: '--radius-md',
    lg: '--radius-lg',
    xl: '--radius-xl',
    '2xl': '--radius-2xl',
    full: '--radius-full',
  },
  spacing: {
    '0': '--space-0',
    '1': '--space-1',
    '2': '--space-2',
    '3': '--space-3',
    '4': '--space-4',
    '5': '--space-5',
    '6': '--space-6',
    '7': '--space-7',
    '8': '--space-8',
    '9': '--space-9',
    '10': '--space-10',
    '11': '--space-11',
    '12': '--space-12',
    '14': '--space-14',
    '16': '--space-16',
    '20': '--space-20',
    '24': '--space-24',
    '28': '--space-28',
    '32': '--space-32',
    '36': '--space-36',
    '40': '--space-40',
    '44': '--space-44',
    '48': '--space-48',
    '52': '--space-52',
    '56': '--space-56',
    '60': '--space-60',
    '64': '--space-64',
    '72': '--space-72',
    '80': '--space-80',
    '96': '--space-96',
  },
  shadow: {
    '2xs': '--shadow-2xs',
    xs: '--shadow-xs',
    sm: '--shadow-sm',
    base: '--shadow',
    md: '--shadow-md',
    lg: '--shadow-lg',
    xl: '--shadow-xl',
    '2xl': '--shadow-2xl',
    elevation: {
      '1': '--shadow-elevation-1',
      '2': '--shadow-elevation-2',
      '3': '--shadow-elevation-3',
      '4': '--shadow-elevation-4',
      '5': '--shadow-elevation-5',
    },
    primaryGlow: '--shadow-primary-glow',
    primaryHover: '--shadow-primary-hover',
  },
} as const;

export const DEFAULT_LIGHT_THEME: ThemeDefinition = {
  id: 'theme_default_light',
  name: 'Default Light',
  mode: 'light',
  version: '1.0.0',
  tokens: {
    colors: {
      background: '216 100% 98%',
      foreground: '220 52% 8%',
      border: '220 13% 91%',
      input: '220 14% 92%',
      ring: '207 100% 50%',
      card: {
        base: '0 0% 100%',
        foreground: '220 52% 8%',
        border: '220 13% 91%',
      },
      popover: {
        base: '0 0% 100%',
        foreground: '220 52% 8%',
        border: '220 13% 91%',
      },
      primary: {
        base: '207 100% 50%',
        foreground: '0 0% 100%',
      },
      secondary: {
        base: '220 14% 96%',
        foreground: '220 52% 8%',
      },
      muted: {
        base: '220 14% 96%',
        foreground: '220 9% 46%',
      },
      accent: {
        base: '207 90% 95%',
        foreground: '207 100% 35%',
      },
      destructive: {
        base: '0 84% 60%',
        foreground: '0 0% 100%',
      },
      sidebar: {
        base: '0 0% 100%',
        foreground: '220 52% 8%',
        border: '220 13% 91%',
        primary: '207 100% 50%',
        primaryForeground: '0 0% 100%',
        accent: '207 90% 95%',
        accentForeground: '207 100% 35%',
        ring: '207 100% 50%',
      },
      chart: {
        '1': '207 100% 50%',
        '2': '160 84% 39%',
        '3': '43 96% 56%',
        '4': '280 67% 52%',
        '5': '340 82% 52%',
      },
      outline: {
        button: 'rgba(0, 0, 0, 0.1)',
        badge: 'rgba(0, 0, 0, 0.05)',
      },
      elevate: {
        '1': 'rgba(0, 0, 0, 0.04)',
        '2': 'rgba(0, 0, 0, 0.1)',
      },
      opaqueButtonBorderIntensity: '-8',
    },
    typography: {
      fontFamily: {
        // Non-Latin faces trail the Latin ones — see the --font-sans comment in index.css.
        // These tokens are written to the CSS variables at runtime, so they must carry
        // the same fallbacks or they silently undo the stylesheet.
        sans: "'Inter', 'Noto Sans HK', 'PingFang HK', 'Microsoft JhengHei', 'Noto Sans Khmer', 'Khmer UI', 'Noto Sans Myanmar', 'Myanmar Text', system-ui, sans-serif",
        serif: 'Georgia, serif',
        mono: "'JetBrains Mono', Menlo, 'Noto Sans HK', 'PingFang HK', 'Microsoft JhengHei', 'Noto Sans Khmer', 'Khmer UI', 'Noto Sans Myanmar', 'Myanmar Text', monospace",
      },
      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.875rem',
        '4xl': '2.25rem',
        '5xl': '3rem',
        '6xl': '3.75rem',
        '7xl': '4.5rem',
        '8xl': '6rem',
        '9xl': '8rem',
      },
      lineHeight: {
        xs: '1rem',
        sm: '1.25rem',
        base: '1.5rem',
        lg: '1.75rem',
        xl: '1.75rem',
        '2xl': '2rem',
        '3xl': '2.25rem',
        '4xl': '2.5rem',
        '5xl': '1',
        '6xl': '1',
        '7xl': '1',
        '8xl': '1',
        '9xl': '1',
      },
      fontWeight: {
        regular: '400',
        medium: '500',
        semibold: '600',
        bold: '700',
      },
      letterSpacing: {
        tight: '-0.01em',
        normal: '0em',
        wide: '0.02em',
      },
    },
    radius: {
      sm: '0.1875rem',
      md: '0.375rem',
      lg: '0.5625rem',
      xl: '0.75rem',
      '2xl': '1rem',
      full: '9999px',
    },
    spacing: {
      '0': '0rem',
      '1': '0.25rem',
      '2': '0.5rem',
      '3': '0.75rem',
      '4': '1rem',
      '5': '1.25rem',
      '6': '1.5rem',
      '7': '1.75rem',
      '8': '2rem',
      '9': '2.25rem',
      '10': '2.5rem',
      '11': '2.75rem',
      '12': '3rem',
      '14': '3.5rem',
      '16': '4rem',
      '20': '5rem',
      '24': '6rem',
      '28': '7rem',
      '32': '8rem',
      '36': '9rem',
      '40': '10rem',
      '44': '11rem',
      '48': '12rem',
      '52': '13rem',
      '56': '14rem',
      '60': '15rem',
      '64': '16rem',
      '72': '18rem',
      '80': '20rem',
      '96': '24rem',
    },
    shadow: {
      '2xs': '0 1px 2px 0 rgba(0, 0, 0, 0.04)',
      xs: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
      sm: '0 2px 4px -1px rgba(0, 0, 0, 0.08), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
      base: '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
      md: '0 6px 12px -2px rgba(0, 0, 0, 0.1), 0 3px 6px -3px rgba(0, 0, 0, 0.08)',
      lg: '0 12px 24px -4px rgba(0, 0, 0, 0.12), 0 6px 12px -6px rgba(0, 0, 0, 0.1)',
      xl: '0 20px 40px -8px rgba(0, 0, 0, 0.14), 0 10px 20px -10px rgba(0, 0, 0, 0.1)',
      '2xl': '0 32px 64px -12px rgba(0, 0, 0, 0.2)',
      elevation: {
        '1': '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
        '2': '0 3px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
        '3': '0 6px 16px rgba(0, 0, 0, 0.12), 0 3px 8px rgba(0, 0, 0, 0.08)',
        '4': '0 12px 28px rgba(0, 0, 0, 0.14), 0 6px 16px rgba(0, 0, 0, 0.08)',
        '5': '0 20px 48px rgba(0, 0, 0, 0.16), 0 10px 24px rgba(0, 0, 0, 0.1)',
      },
      primaryGlow: '0 4px 14px -3px rgba(0, 140, 255, 0.4)',
      primaryHover: '0 6px 20px -4px rgba(0, 140, 255, 0.5)',
    },
  },
};
export const DEFAULT_DARK_THEME: ThemeDefinition = {
  id: 'theme_default_dark',
  name: 'Default Dark',
  mode: 'dark',
  version: '1.0.0',
  tokens: {
    colors: {
      background: '220 13% 10%',
      foreground: '216 100% 97%',
      border: '220 13% 20%',
      input: '220 13% 18%',
      ring: '207 100% 50%',
      card: {
        base: '220 13% 14%',
        foreground: '216 100% 97%',
        border: '220 13% 20%',
      },
      popover: {
        base: '220 13% 14%',
        foreground: '216 100% 97%',
        border: '220 13% 20%',
      },
      primary: {
        base: '207 100% 50%',
        foreground: '220 13% 10%',
      },
      secondary: {
        base: '220 13% 23%',
        foreground: '216 100% 97%',
      },
      muted: {
        base: '220 13% 23%',
        foreground: '220 9% 64%',
      },
      accent: {
        base: '207 90% 30%',
        foreground: '207 100% 92%',
      },
      destructive: {
        base: '0 84% 60%',
        foreground: '220 13% 10%',
      },
      sidebar: {
        base: '220 13% 12%',
        foreground: '216 100% 97%',
        border: '220 13% 20%',
        primary: '207 100% 50%',
        primaryForeground: '220 13% 10%',
        accent: '207 90% 30%',
        accentForeground: '207 100% 92%',
        ring: '207 100% 50%',
      },
      chart: {
        '1': '207 100% 50%',
        '2': '160 84% 39%',
        '3': '43 96% 56%',
        '4': '280 67% 52%',
        '5': '340 82% 52%',
      },
      outline: {
        button: 'rgba(255, 255, 255, 0.2)',
        badge: 'rgba(255, 255, 255, 0.1)',
      },
      elevate: {
        '1': 'rgba(255, 255, 255, 0.05)',
        '2': 'rgba(255, 255, 255, 0.1)',
      },
      opaqueButtonBorderIntensity: '8',
    },
    typography: {
      fontFamily: {
        // Non-Latin faces trail the Latin ones — see the --font-sans comment in index.css.
        // These tokens are written to the CSS variables at runtime, so they must carry
        // the same fallbacks or they silently undo the stylesheet.
        sans: "'Inter', 'Noto Sans HK', 'PingFang HK', 'Microsoft JhengHei', 'Noto Sans Khmer', 'Khmer UI', 'Noto Sans Myanmar', 'Myanmar Text', system-ui, sans-serif",
        serif: 'Georgia, serif',
        mono: "'JetBrains Mono', Menlo, 'Noto Sans HK', 'PingFang HK', 'Microsoft JhengHei', 'Noto Sans Khmer', 'Khmer UI', 'Noto Sans Myanmar', 'Myanmar Text', monospace",
      },
      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.875rem',
        '4xl': '2.25rem',
        '5xl': '3rem',
        '6xl': '3.75rem',
        '7xl': '4.5rem',
        '8xl': '6rem',
        '9xl': '8rem',
      },
      lineHeight: {
        xs: '1rem',
        sm: '1.25rem',
        base: '1.5rem',
        lg: '1.75rem',
        xl: '1.75rem',
        '2xl': '2rem',
        '3xl': '2.25rem',
        '4xl': '2.5rem',
        '5xl': '1',
        '6xl': '1',
        '7xl': '1',
        '8xl': '1',
        '9xl': '1',
      },
      fontWeight: {
        regular: '400',
        medium: '500',
        semibold: '600',
        bold: '700',
      },
      letterSpacing: {
        tight: '-0.01em',
        normal: '0em',
        wide: '0.02em',
      },
    },
    radius: {
      sm: '0.1875rem',
      md: '0.375rem',
      lg: '0.5625rem',
      xl: '0.75rem',
      '2xl': '1rem',
      full: '9999px',
    },
    spacing: {
      '0': '0rem',
      '1': '0.25rem',
      '2': '0.5rem',
      '3': '0.75rem',
      '4': '1rem',
      '5': '1.25rem',
      '6': '1.5rem',
      '7': '1.75rem',
      '8': '2rem',
      '9': '2.25rem',
      '10': '2.5rem',
      '11': '2.75rem',
      '12': '3rem',
      '14': '3.5rem',
      '16': '4rem',
      '20': '5rem',
      '24': '6rem',
      '28': '7rem',
      '32': '8rem',
      '36': '9rem',
      '40': '10rem',
      '44': '11rem',
      '48': '12rem',
      '52': '13rem',
      '56': '14rem',
      '60': '15rem',
      '64': '16rem',
      '72': '18rem',
      '80': '20rem',
      '96': '24rem',
    },
    shadow: {
      '2xs': '0 1px 2px 0 rgba(0, 0, 0, 0.12)',
      xs: '0 1px 3px 0 rgba(0, 0, 0, 0.16), 0 1px 2px -1px rgba(0, 0, 0, 0.12)',
      sm: '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 1px 2px -1px rgba(0, 0, 0, 0.16)',
      base: '0 4px 6px -1px rgba(0, 0, 0, 0.24), 0 2px 4px -2px rgba(0, 0, 0, 0.16)',
      md: '0 6px 12px -2px rgba(0, 0, 0, 0.28), 0 3px 6px -3px rgba(0, 0, 0, 0.2)',
      lg: '0 12px 24px -4px rgba(0, 0, 0, 0.32), 0 6px 12px -6px rgba(0, 0, 0, 0.24)',
      xl: '0 20px 40px -8px rgba(0, 0, 0, 0.4), 0 10px 20px -10px rgba(0, 0, 0, 0.28)',
      '2xl': '0 32px 64px -12px rgba(0, 0, 0, 0.48)',
      elevation: {
        '1': '0 1px 3px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.16)',
        '2': '0 3px 6px rgba(0, 0, 0, 0.24), 0 2px 4px rgba(0, 0, 0, 0.16)',
        '3': '0 6px 16px rgba(0, 0, 0, 0.28), 0 3px 8px rgba(0, 0, 0, 0.2)',
        '4': '0 12px 28px rgba(0, 0, 0, 0.32), 0 6px 16px rgba(0, 0, 0, 0.24)',
        '5': '0 20px 48px rgba(0, 0, 0, 0.4), 0 10px 24px rgba(0, 0, 0, 0.28)',
      },
      primaryGlow: '0 4px 14px -3px rgba(0, 140, 255, 0.5)',
      primaryHover: '0 6px 20px -4px rgba(0, 140, 255, 0.6)',
    },
  },
};
